import { IonContent, IonImg } from '@ionic/react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { usePublicDoc } from '../../api/hooks/sales/usePublicDoc';
import { useCompanyLogo } from '../../api/hooks/useCompanyLogo';
import { InvoicePdf, pdfStyles } from '../../components';
import { LoadingScreen } from '../../components/atoms/LoadingScreen';
import { DocView } from '../../components/molecules/docView/DocView';
import { EN_LANGUAGE } from '../../config/i18n/common';
import { COUNTRY_CODE } from '../../interfaces/customer.interface';
import logo from '../../theme/assets/logo.webp';
import {
  SaleContextInterface,
  SaleScreenContext,
  SaleScreenContextProvider,
} from '../Sales/components/SaleScreenContext';

export const PublicDocScreen = () => {
  return (
    <SaleScreenContext>
      <PublicDocScreenContent />
    </SaleScreenContext>
  );
};

const PublicDocScreenContent = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { data, isFetched, isError, isLoading } = usePublicDoc(id);
  const { setDoc } = useContext<SaleContextInterface>(SaleScreenContextProvider);
  const { data: logoData, isFetched: isFetchedLogo } = useCompanyLogo(data?.data?.company?.logoUrl || '');
  const isLogoReady = useMemo(() => {
    if (data?.data?.company?.logoUrl) {
      if (isFetchedLogo) return true;
      return false;
    }

    return true;
  }, [data?.data?.company?.logoUrl, isFetchedLogo]);

  useEffect(() => {
    if (data?.data) {
      setDoc?.(data?.data);
    }

    return () => {
      setDoc?.(null);
    };
  }, [data, setDoc]);

  useEffect(() => {
    if (isFetched && data?.data) {
      const countryCode = data?.data?.customer?.country?.code;
      if (countryCode && countryCode !== COUNTRY_CODE.GR) {
        i18n.changeLanguage(EN_LANGUAGE.value);
      }
    }
  }, [data?.data, data?.data?.customer?.country?.code, i18n, isFetched]);

  const getLogo = useCallback(() => {
    return (
      <div className="flex-center-center-column-nowrap">
        <IonImg src={logo} style={styles.logo} />
      </div>
    );
  }, []);

  const getError = useCallback(() => {
    if ((!data?.success && data?.errorCode) || isError) {
      return (
        <div className="normalText6 max-width-460px text-center padding-t-100">
          {t('API_ERRORS.UNABLE_TO_GET_PUBLIC_DOC')}
        </div>
      );
    }
  }, [data?.errorCode, data?.success, isError, t]);

  const downloadDoc = useCallback(() => {
    if (isFetched && data?.success && data?.data?.id && data?.data?.company && isLogoReady) {
      const localDoc = data?.data;
      const fileName = `billease-${localDoc?.customer?.companyName}-${localDoc?.documentDate}.pdf`;

      return (
        <div className="padding-b-10">
          <PDFDownloadLink
            document={<InvoicePdf doc={data?.data} logo={logoData} />}
            fileName={fileName}
            style={pdfStyles.ionButtonOutline}>
            {t('FORMS.DOWNLOAD_PDF')}
          </PDFDownloadLink>
        </div>
      );
    }
  }, [isFetched, isLogoReady, data?.success, data?.data, logoData, t]);

  const getDocView = useCallback(() => {
    if (data?.success && data?.data && isFetched) {
      return <DocView doc={data?.data} logo={logoData} isPublic />;
    }
  }, [data?.success, data?.data, logoData, isFetched]);

  const getLoading = useCallback(() => {
    if (isLoading) {
      return <LoadingScreen />;
    }

    return null;
  }, [isLoading]);

  return (
    <IonContent scrollY={true}>
      {getLoading()}
      <div className="flex-center-center-column-nowrap padding-l-20 padding-r-20">
        {getLogo()}
        {downloadDoc()}
        {getError()}
        <div className="scrollable-doc">{getDocView()}</div>
      </div>
    </IonContent>
  );
};

const styles = {
  logo: {
    width: 200,
    height: 100,
  },
};
